/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps */
// src/hooks/useTextEmbeddings.js
/**
 * Backend-only embedding/projection hook.
 *
 * This hook no longer supports local embedding generation or local PCA.
 * It loads pre-projected coordinates from the backend and fetches the
 * corresponding PCA basis metadata for matrix display/save flows.
 */

import { useState, useCallback } from "react";

type ProjectedChunkRow = {
  chunk_id: string;
  chunk_index: number;
  chunk_text: string;
  chunk_label: string;
  chunk_tokens: unknown;
  x: number;
  y: number;
  z: number;
  source_text_id: string;
  window_size: number;
};

type ProjectedChunkRowWithSourceOrder = ProjectedChunkRow & {
  __sourceOrder: number;
};

function parseChunkTokens(value: unknown): Array<{ id: number; text: string }> {
  if (Array.isArray(value)) {
    return value as Array<{ id: number; text: string }>;
  }
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

/**
 * @typedef {import('./chunk').Chunk} Chunk
 * @typedef {import('./token').Token} Token
 * @typedef {import('./embedding').Embedding} Embedding
 * @typedef {import('./pca').PCA} PCA
 */

/**
 * @param {Object} params
 * @param {number} params.embeddingDimensions
 *
 * @returns {{
 *   enrichedChunks: Chunk[],
 *   isLoading: boolean,
 *   processChunks: (chunks: Chunk[], opts?: { chunkSetId?: string, pcaBasisId?: string }) => Promise<void>,
 *   pcaResult: PCA | null,
 *   status: "idle"|"loading"|"complete"|"error",
 *   progress: { total: number, done: number }
 * }}
 */
export function useTextEmbeddings({ embeddingDimensions }: { embeddingDimensions: number }) {
  const [isLoading, setIsLoading] = useState(false);
  const [pcaResult, setPcaResult] = useState(null);
  const [enrichedChunks, setEnrichedChunks] = useState([]);
  const [status, setStatus] = useState("idle");
  const [progress, setProgress] = useState({ total: 0, done: 0 });
  const PAGE_LIMIT = 1000;

  const fetchProjectedRowsForChunkSet = useCallback(async ({
    chunkSetId,
    pcaBasisId,
  }: {
    chunkSetId: string;
    pcaBasisId: string;
  }) => {
    const allRows: ProjectedChunkRow[] = [];
    let after: number | undefined = undefined;

    while (true) {
      const query = new URLSearchParams({
        chunk_set_id: chunkSetId,
        pca_basis_id: pcaBasisId,
        limit: String(PAGE_LIMIT),
      });

      if (after != null) {
        query.set("after", String(after));
      }

      const rowsRes = await fetch(`/api/projected-chunks?${query.toString()}`);
      if (!rowsRes.ok) {
        throw new Error(`[useTextEmbeddings:api] HTTP ${rowsRes.status} from projected-chunks`);
      }

      const payload = await rowsRes.json();
      if (!Array.isArray(payload)) {
        throw new Error("[useTextEmbeddings:api] Expected array from projected-chunks");
      }

      const rows = payload as ProjectedChunkRow[];

      allRows.push(...rows);

      if (rows.length < PAGE_LIMIT) {
        break;
      }

      const lastRow = rows[rows.length - 1];
      const lastChunkIndex = Number((lastRow as { chunk_index?: unknown }).chunk_index);
      if (!Number.isFinite(lastChunkIndex)) {
        break;
      }
      after = lastChunkIndex;
    }

    return allRows;
  }, []);

  const fetchPcaBasis = useCallback(async (pcaBasisId) => {
    const res = await fetch(`/api/pca-bases/${encodeURIComponent(pcaBasisId)}`);
    if (!res.ok) {
      throw new Error(`[useTextEmbeddings:api] HTTP ${res.status} from pca-bases/:id`);
    }
    const basis = await res.json();
    return {
      pcaMatrix: basis.pca_matrix ?? null,
      pcaMeanVector: basis.pca_mean_vector ?? null,
      pcaMeanVectorDimensions: Array.isArray(basis.pca_mean_vector)
        ? basis.pca_mean_vector.length
        : 0,
    };
  }, []);

  /**
   * Process chunk sets in backend mode.
   * Fetches pre-projected 3D coordinates and merges all rows into one renderable set,
   * then loads the corresponding PCA basis matrix for downstream UI.
   *
   * @param {{ chunkSetIds: string[], pcaBasisId: string }} opts
   */
  const processChunksApi = useCallback(async ({ chunkSetIds, pcaBasisId }: { chunkSetIds: string[]; pcaBasisId: string }) => {
    setIsLoading(true);
    setStatus("loading");
    setProgress({ total: 0, done: 0 });
    try {
      const uniqueChunkSetIds: string[] = Array.from(new Set(chunkSetIds));
      const projectedRowsPromises = uniqueChunkSetIds.map(async (chunkSetId, sourceOrder) => {
        const rows = await fetchProjectedRowsForChunkSet({ chunkSetId, pcaBasisId });
        return rows.map((row) => ({ ...row, __sourceOrder: sourceOrder }));
      });

      const [projectedRowsList, pcaBasis] = await Promise.all([
        Promise.all(projectedRowsPromises),
        fetchPcaBasis(pcaBasisId),
      ]);

      const rows = projectedRowsList
        .flat()
        .sort((a, b) => {
          if (a.__sourceOrder !== b.__sourceOrder) {
            return a.__sourceOrder - b.__sourceOrder;
          }
          return Number(a.chunk_index) - Number(b.chunk_index);
        }) as ProjectedChunkRowWithSourceOrder[];

      if (!Array.isArray(rows)) {
        throw new Error("[useTextEmbeddings:api] Expected array of merged projected chunks");
      }

      const finalChunks = rows.map((row) => ({
        chunkID: row.chunk_id,
        chunkIndex: row.chunk_index,
        chunkText: row.chunk_text,
        chunkLabel: row.chunk_label,
        chunkTokens: parseChunkTokens(row.chunk_tokens),
        chunkWindowSize: Number(row.window_size),
        sourceTextId: row.source_text_id,
        chunkEmbeddingReduced: {
          fullEmbedding: [row.x, row.y, row.z],
          dimensions: 3,
        },
      }));

      setEnrichedChunks(finalChunks);
      setPcaResult(pcaBasis);
      setProgress({ total: finalChunks.length, done: finalChunks.length });
      setStatus("complete");
    } catch (err) {
      console.error("[useTextEmbeddings:processChunksApi] Error:", err);
      setEnrichedChunks([]);
      setPcaResult(null);
      setStatus("error");
    } finally {
      setIsLoading(false);
    }
  }, [fetchPcaBasis, fetchProjectedRowsForChunkSet]);

  /**
   * Process projected chunks for one or more backend chunk sets projected into a PCA basis.
   *
   * @param {{ chunkSetId?: string, chunkSetIds?: string[], pcaBasisId?: string }} [opts]
   */
  const processChunks = useCallback(
    async (opts: { chunkSetId?: string; chunkSetIds?: string[]; pcaBasisId?: string } = {}) => {
      const { chunkSetId, chunkSetIds = [], pcaBasisId } = opts;
      const ids = chunkSetIds.length > 0 ? chunkSetIds : (chunkSetId ? [chunkSetId] : []);
      if (ids.length === 0 || !pcaBasisId) {
        console.warn("[useTextEmbeddings:processChunks] backend mode requires chunkSetId(s) and pcaBasisId");
        return;
      }
      await processChunksApi({ chunkSetIds: ids, pcaBasisId });
    },
    [processChunksApi]
  );

  return {
    enrichedChunks,
    isLoading,
    processChunks,
    pcaResult,
    status,
    progress,
  };
}
